import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DataRole, DateTime } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { DomainResult } from '@mode-switch/query';
import {
  EncDiscreteBooleanFilterField,
  EncDiscreteFilterField,
  EncFilterField,
  EncSelectionCondition,
  Encoding,
  SelectionMode,
} from '@mode-vegas/vegas';
import { IconComponent, SearchInputComponent, TooltipDirective, trackByFactory } from '@mode/capra';
import { ChartFacade } from '@mode/charts/contract';
import { ChartDate, FieldFormatHelpers } from '@mode/charts/util-vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { FlamingoResponse } from '@mode/shared/contract-common';
import { LoaderComponent } from '@mode/shared/ui';
import { formatInUTC, isPresent } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { sortBy } from 'lodash';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  debounce,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  startWith,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import * as util from '../../../filter-modal-helpers';

interface FilterValueItem {
  value: number | string | boolean | null;
  label: string;
  selected: boolean;
}

/**
 * This component displays a list of filter values which the user
 * can individually select items to include/exclude.
 */
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconComponent,
    LoaderComponent,
    SearchInputComponent,
    ScrollingModule,
    TooltipDirective,
  ],
  selector: 'mode-filter-value-list',
  templateUrl: './filter-value-list.component.html',
  styleUrls: ['./filter-value-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterValueListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() field!: CompiledField;
  @Input() filter!: EncDiscreteFilterField | EncDiscreteBooleanFilterField;
  @Input() view!: FlamingoTypes.ModeView;
  @Input() encoding!: Encoding;
  @Input() queryRunToken!: string;

  @Output() filterChange = new EventEmitter<EncFilterField>();
  @Output() isInvalid = new EventEmitter<boolean>();

  @ViewChild('selectAllInput') selectAllInput: ElementRef | undefined;

  trackByValue = trackByFactory<FilterValueItem>(({ value }) => value);

  private readonly allValuesRefId = 'filter-domain-all-values';
  private readonly relevantValuesRefId = 'filter-domain-relevant-values';
  private domainAliasSubject$ = new BehaviorSubject<string | undefined>(undefined);
  private domainAlias$: Observable<string>;

  searchTerm = '';
  filteredValueList: Array<FilterValueItem> = [];
  unfilteredValueList: Array<FilterValueItem> = [];
  allDomainResults: ReadonlyArray<number | string | boolean | null> | undefined;
  relevantDomainResults: ReadonlyArray<number | string | boolean | null> | undefined;
  hasLoadError = false;

  isAll = false;
  hasPartialSelection = false;
  isExclude = false;
  filterDescription = 'Loading filter values';

  visibleFilterValues$ = new BehaviorSubject<Array<FilterValueItem>>([]);
  searchTerm$ = new BehaviorSubject('');
  isLoadingSubject$ = new BehaviorSubject<boolean>(true);
  showLoader$: Observable<boolean>;
  private allValuesResult$: Observable<DomainResult.Discrete<DataRole>>;
  private relevantValuesResult$: Observable<DomainResult.Discrete<DataRole>>;
  private destroyed$ = new Subject<void>();

  private showRelevantValuesOnlySubject$ = new BehaviorSubject<boolean>(false);
  private showRelevantValuesOnly$: Observable<boolean>;
  showRelevantValuesOnly = false;

  constructor(private chartFacade: ChartFacade) {
    this.domainAlias$ = this.domainAliasSubject$.pipe(filter(isPresent), distinctUntilChanged());

    this.showRelevantValuesOnly$ = this.showRelevantValuesOnlySubject$.pipe(distinctUntilChanged());

    this.showLoader$ = this.isLoadingSubject$.pipe(
      debounce((showLoader) => {
        return showLoader ? timer(350) : EMPTY;
      })
    );

    this.allValuesResult$ = this.domainAlias$.pipe(
      switchMap((domainAlias) =>
        this.chartFacade.createSelectDomainValues(this.allValuesRefId, domainAlias).pipe(
          tap(() => (this.allDomainResults = undefined)),
          filter(
            (domainResult): domainResult is DomainResult.Discrete<DataRole> => domainResult?.type === Attr.Type.Discrete
          )
        )
      )
    );

    this.relevantValuesResult$ = this.domainAlias$.pipe(
      switchMap((domainAlias) =>
        this.chartFacade.createSelectDomainValues(this.relevantValuesRefId, domainAlias).pipe(
          tap(() => (this.relevantDomainResults = undefined)),
          filter(
            (domainResult): domainResult is DomainResult.Discrete<DataRole> => domainResult?.type === Attr.Type.Discrete
          )
        )
      )
    );
  }

  ngOnInit(): void {
    // Handle any query errors for all values requests
    this.chartFacade
      .createErrorFromDomainQuery(this.allValuesRefId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => this.onLoadError(error));

    // Handle any query errors for relevant values requests
    this.chartFacade
      .createErrorFromDomainQuery(this.relevantValuesRefId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((error) => this.onLoadError(error));

    // Fetch all values when the formula changes
    this.domainAlias$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.fetchAllDomainValues();
    });

    // Fetch relevant values when the formula changes and showRelevantValues is true.
    // Should only fetch relevant values _once_ per new formula
    this.domainAlias$
      .pipe(
        switchMap(() =>
          this.showRelevantValuesOnly$.pipe(
            filter((show) => show),
            first()
          )
        ),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.fetchRelevantDomainValues();
      });

    // Toggling show relevant values should trigger a rendering of all or relevant results,
    // depending on the stage of the flag. If results are not set then a domain request may
    // be in flight, after which we'll render the results
    this.showRelevantValuesOnly$.pipe(takeUntil(this.destroyed$)).subscribe((showRelevant) => {
      if (showRelevant && this.relevantDomainResults) {
        this.makeValueList(this.relevantDomainResults);
      }

      const showAll = !showRelevant;
      if (showAll && this.allDomainResults) {
        this.makeValueList(this.allDomainResults);
      }
    });

    // Handle results from 'all' and 'relevant' values queries. We always issue a query for all
    // of the values. We may or may not issue a query for the relevant values. Need to wait
    // for all of the required queries to complete before hiding the loader.
    const optionalRelevantValues$ = this.relevantValuesResult$.pipe(startWith(undefined));
    combineLatest([this.allValuesResult$, optionalRelevantValues$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([allValues, relevantValues]) => {
        this.allDomainResults = allValues.values;
        this.relevantDomainResults = relevantValues?.values;

        if (this.showRelevantValuesOnly && this.relevantDomainResults) {
          this.makeValueList(this.relevantDomainResults);
        }

        const showAll = !this.showRelevantValuesOnly;
        if (showAll && this.allDomainResults) {
          this.makeValueList(this.allDomainResults);
        }

        this.isLoadingSubject$.next(false);
      });

    this.searchTerm$
      .pipe(
        takeUntil(this.destroyed$),
        filter((searchTerm) => searchTerm != null),
        debounceTime(400)
      )
      .subscribe((searchTerm: string) => {
        this.searchTerm = searchTerm;
        const lowercasedTerm = searchTerm.toLowerCase();
        this.filteredValueList = this.unfilteredValueList.filter((filterValue) =>
          filterValue.label.toLowerCase().includes(lowercasedTerm)
        );
        this.visibleFilterValues$.next(this.filteredValueList);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter) {
      const currentSelection = this.filter.selection;
      this.isAll = currentSelection.mode === SelectionMode.ALL || currentSelection.mode === SelectionMode.EXCLUDE_ALL;
      this.isExclude =
        currentSelection.mode === SelectionMode.EXCLUDE || currentSelection.mode === SelectionMode.EXCLUDE_ALL;
      this.hasPartialSelection = !this.isAll && (currentSelection.selected ?? []).length > 0;

      // Set the checkbox states to match the filter. Use a Set for faster lookups.
      const selected = new Set(currentSelection.selected);
      this.unfilteredValueList.forEach((filterValue) => {
        filterValue.selected = this.isAll || selected.has(filterValue.value);
      });

      this.showRelevantValuesOnly = this.filter.showRelevantValuesOnly ?? false;
      this.showRelevantValuesOnlySubject$.next(this.showRelevantValuesOnly);

      const domainAlias = util.idFromFormula(this.filter.formula);
      this.domainAliasSubject$.next(domainAlias);

      this.setFilterDescription(
        this.isAll ? this.unfilteredValueList.length : selected.size,
        this.unfilteredValueList.length,
        this.allDomainResults?.length ?? 0
      );
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectAll() {
    const allStatesMatch = this.allSelectedStatesMatch(this.filteredValueList);
    if (allStatesMatch) {
      // Toggle the selection
      const isSelected = this.filteredValueList[0]?.selected ?? false;
      if (isSelected) {
        this.deselectAll();
      } else {
        this.selectAll();
      }
    } else {
      // Mix of selected/unselected. Default behavior is to deselect all
      this.deselectAll();
    }
    this.onSelectionChange();
    if (this.selectAllInput?.nativeElement !== undefined) {
      // The checkbox makes the selection/deselection regardless of what
      // the model says. We need to make sure that it is correct.
      this.selectAllInput.nativeElement['checked'] = this.isAll;
    }
  }

  onSelectItem() {
    this.onSelectionChange();
  }

  onLoadError(error: FlamingoResponse.FlamingoErrorResponse | undefined) {
    if (error !== undefined) {
      // This filter is useless without the list of possible values.
      // Show an error message and block input to the other controls
      // and the Apply actions.
      this.hasLoadError = true;
      this.filterDescription = 'An error occurred while loading filter values';
      this.isLoadingSubject$.next(false);
      this.isInvalid.emit(true);
      console.warn(`Unable to load filter data range. Error: ${JSON.stringify(error)}`);
    }
  }

  fetchAllDomainValues() {
    this.fetchDomainValues(this.allValuesRefId, false);
  }

  fetchRelevantDomainValues() {
    this.fetchDomainValues(this.relevantValuesRefId, true);
  }

  fetchDomainValues(refId: string, relevantValuesOnly: boolean) {
    this.isLoadingSubject$.next(true);
    this.domainAlias$
      .pipe(first())
      .subscribe((domainAlias) =>
        this.chartFacade.fetchFilterDomainValues(
          this.queryRunToken,
          this.view,
          this.encoding,
          this.field,
          refId,
          domainAlias,
          relevantValuesOnly
        )
      );
  }

  deselectAll() {
    this.filteredValueList.forEach((filterValue) => (filterValue.selected = false));
  }

  selectAll() {
    this.filteredValueList.forEach((filterValue) => (filterValue.selected = true));
  }

  onSearchTermChanged(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  onSelectionChange() {
    const selectedList = this.unfilteredValueList.filter((filterValue) => filterValue.selected);

    this.isAll = selectedList.length === (this.allDomainResults?.length ?? 0);
    this.hasPartialSelection = !this.isAll && selectedList.length > 0;

    const selectionCondition = this.buildSelectionCondition();
    const updatedFilter = {
      ...this.filter,
      selection: selectionCondition,
      showRelevantValuesOnly: this.showRelevantValuesOnly,
    };
    this.setFilterDescription(selectedList.length, this.unfilteredValueList.length, this.allDomainResults?.length ?? 0);
    this.filterChange.emit(updatedFilter);
  }

  buildSelectionCondition(): EncSelectionCondition {
    if (this.isAll) {
      return {
        selected: [],
        mode: this.isExclude ? SelectionMode.EXCLUDE_ALL : SelectionMode.ALL,
      };
    }

    const selectedList = this.unfilteredValueList
      .filter((filterValue) => filterValue.selected)
      .map((filterValue) => (filterValue.value == null ? null : filterValue.value));

    return {
      selected: selectedList,
      mode: this.isExclude ? SelectionMode.EXCLUDE : SelectionMode.INCLUDE,
    };
  }

  makeValueList(domainValues: ReadonlyArray<number | string | boolean | null>) {
    const selectionCondition = this.filter.selection;
    const selected = new Set(selectionCondition?.selected ?? []);
    const results = sortBy(domainValues).map((value: string | number | boolean | null) => {
      return {
        value: value,
        label: this.formatValue(value),
        selected: this.isAll || selected.has(value),
      };
    });
    this.unfilteredValueList = results;
    this.filteredValueList = this.unfilteredValueList.filter((filterValue) =>
      filterValue.label.includes(this.searchTerm)
    );
    const selectedList = this.unfilteredValueList.filter((filterValue) => filterValue.selected);
    this.setFilterDescription(selectedList.length, this.unfilteredValueList.length, this.allDomainResults?.length ?? 0);
    this.onSelectionChange();
    this.visibleFilterValues$.next(this.filteredValueList);
  }

  formatValue(value: unknown): string {
    if (value == null) {
      return 'Null';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (this.field.yearStart) {
      switch (this.field.dateFuncUnit) {
        case DateTime.Unit.Year:
        case DateTime.Unit.Quarter: {
          return FieldFormatHelpers.formatValue(value, this.field);
        }
      }
    }

    if (this.field.hasDatePart) {
      return ChartDate.makeDatePartName(this.field, value);
    }

    if (this.field.isFormulaDate) {
      return formatInUTC(new Date(+value), 'yyyy-MM-dd HH:mm:ss');
    }

    return String(value);
  }

  private setFilterDescription(numSelected: number, totalItems: number, numAllValues: number) {
    const excludedSuffix = this.isExclude ? ' that will be excluded' : '';

    let filterText =
      totalItems === 1
        ? `There is ${numSelected} of 1 value selected${excludedSuffix}.`
        : `There are ${numSelected} of ${totalItems} values selected${excludedSuffix}.`;

    const additionalItems = numAllValues - totalItems;
    if (this.showRelevantValuesOnly && additionalItems > 0) {
      filterText =
        additionalItems === 1
          ? `${filterText} One additional value is hidden due to other applied filters.`
          : `${filterText} ${additionalItems} additional values are hidden due to other applied filters.`;
    }

    this.filterDescription = filterText;
  }

  private allSelectedStatesMatch(items: Array<FilterValueItem>) {
    if (items.length === 0) {
      return true;
    }
    const firstState = items[0].selected;
    // Return false if any value is different from the first
    return !items.some((value) => value.selected !== firstState);
  }
}
